import { FunctionComponent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Card, CardContent, Link, Stack, Typography } from "@mui/material";

import AppLogo from "@/assets/images/favicon.png";
import { ROUTE_CONFIG } from "@/routes/config";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";
import { AuthError } from "@/components";

import { ButtonSize } from "@/components/Button/types";

import { Button } from "@/components/Button/Button";

import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";

import { InputField } from "@/components/InputField/InputField";

import { useIsFeatureActiveUnauthenticated } from "@/utils/useIsFeatureActiveUnauthenticated";

import { LoginType, LoginSchema } from "./schema/login";
import { useLoginMutation } from "../../redux/auth.api";

export const Login: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`login.${key}`);
	const SELF_REGISTER = useIsFeatureActiveUnauthenticated(FeaturesCodes.SELF_REGISTER);

	const navigate = useNavigate();

	const [login, { isLoading, error }] = useLoginMutation();

	const initialValues: LoginType = {
		email: "",
		password: "",
	};

	const {
		setValue,
		handleSubmit,
		formState: { errors },
	} = useForm<LoginType>({
		defaultValues: initialValues,
		resolver: zodResolver(LoginSchema),
	});

	const [currentFormState, setCurrentFormState] = useState<Partial<LoginType>>(initialValues);

	const handleSaveFormState = (value: LoginType[keyof LoginType], name: keyof LoginType) => {
		setCurrentFormState({
			...currentFormState,
			[name]: value,
		});

		setValue(name, value, {
			shouldDirty: true,
			shouldValidate: true,
		});
	};

	const formErrors = Object.values(errors).map((error) => error.message) as ERROR_TYPE[];

	const onSubmit = async (values: LoginType) => {
		try {
			await login(values).unwrap();
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<div className="w-full flex flex-col items-center">
			<div className="w-full z-10 flex flex-col justify-center items-center mb-8">
				<img alt="logo" className="w-20 h-20" src={AppLogo} />
				<div className="mb-2 text-md">{ts("welcomeBack")}</div>
				{SELF_REGISTER === true && (
					<div className="mb-2 text-sm">
						{ts("dontHaveAccount")} <Link href="/register">{ts("signUpForFree")}</Link>
					</div>
				)}
			</div>
			<Card
				elevation={16}
				sx={{
					mb: 4,
					position: "relative",
					width: "100%",
					maxWidth: "444px",
				}}
			>
				<CardContent>
					<Stack>
						<Typography mb={2} variant="h6">
							{ts("signInToSignalist")}
						</Typography>

						<Box component="form" onSubmit={handleSubmit(onSubmit)}>
							<InputField
								error={!!errors.email?.message}
								handleChange={(event) => handleSaveFormState(event.target.value, "email")}
								name="email"
								placeholder={t("basics.email")}
								type="email"
								value={currentFormState.email || ""}
							/>
							<InputField
								error={!!errors.password?.message}
								handleChange={(event) => handleSaveFormState(event.target.value, "password")}
								name="password"
								placeholder={t("basics.password")}
								type="password"
								value={currentFormState.password || ""}
							/>

							<Stack>
								<Button
									className="w-full"
									isLoading={isLoading}
									size={ButtonSize.L}
									title={ts("loginCta")}
									type="submit"
								/>

								<Link
									component="a"
									sx={{ mt: 2, textAlign: "center", cursor: "pointer" }}
									underline="hover"
									variant="subtitle2"
									onClick={() => navigate(ROUTE_CONFIG.FORGOT_PASSWORD)}
								>
									{ts("forgotPasswordCta")}
								</Link>
							</Stack>
						</Box>

						{getAllErrors(error, formErrors).length ? (
							<AuthError>{renderErrorMessages(getAllErrors(error, formErrors))}</AuthError>
						) : null}
					</Stack>
				</CardContent>
			</Card>
		</div>
	);
};
